import { Contexts } from 'modules/core/ContextProcessorService';
import { ApplicationCreationFeatures } from './ApplicationCreationFeatures';
import { MessagingHubFeatures } from 'modules/messaginghub/MessagingHubFeatures';
import { BuilderFeatures } from '../detail/portal-submodule-builder/templates/builder/BuilderFeatures';
import CustomerServiceTemplatePT from './templates/customerService/customerServiceTemplate.json';
import CustomerServiceForwardToDeskTemplatePT from './templates/customerService/customerServiceFowardToDeskTemplate.json';
import AiAgentPublishFlowTemplate from './templates/aiAgentPublishFlowTemplate.json';
const NO_MARKETPLACE_TEMPLATE_ID = '0';
export var CreateApplicationSteps;
(function (CreateApplicationSteps) {
    CreateApplicationSteps["DataValidation"] = "data-validation";
    CreateApplicationSteps["InitialCreation"] = "initial-creation";
    CreateApplicationSteps["BuilderActivation"] = "builder-activation";
    CreateApplicationSteps["AttendanceActivation"] = "attendance-activation";
    CreateApplicationSteps["TemplateApplication"] = "template-applying";
})(CreateApplicationSteps || (CreateApplicationSteps = {}));
export var ApplicationType;
(function (ApplicationType) {
    ApplicationType["Master"] = "master";
    ApplicationType["Builder"] = "builder";
    ApplicationType["Pipeline"] = "pipeline";
})(ApplicationType || (ApplicationType = {}));
export class CreateApplicationError extends Error {
    static $inject = ['err', 'step'];
    constructor(err, step) {
        super(err.message);
        this.stack = err.stack;
        this.name = 'CreateApplicationError';
        this.step = step;
    }
}
export class CreateApplicationService {
    static $inject = ['ContextProcessorService', 'ApplicationService2', 'MessagingHubService', 'ChatbotSettingsService', 'InitializeUserService', 'TeamService', 'QueuesService', 'TenantService', 'AttendanceHourService', 'AccountService2', 'MarketplaceTemplatesService', '$rootScope', '$translate', 'AI_AGENT_STORAGE_BASE_URL', 'MEDIA_PROFILE_FOR_AVATARS'];
    // Explicit AngularJS DI annotation for minification safety
    // Order must match constructor parameter order
    constructor(ContextProcessorService, ApplicationService2, MessagingHubService, ChatbotSettingsService, InitializeUserService, TeamService, QueuesService, TenantService, AttendanceHourService, AccountService2, MarketplaceTemplatesService, $rootScope, $translate, AI_AGENT_STORAGE_BASE_URL, MEDIA_PROFILE_FOR_AVATARS) {
        'ngInject';
        this.ContextProcessorService = ContextProcessorService;
        this.ApplicationService2 = ApplicationService2;
        this.MessagingHubService = MessagingHubService;
        this.ChatbotSettingsService = ChatbotSettingsService;
        this.InitializeUserService = InitializeUserService;
        this.TeamService = TeamService;
        this.QueuesService = QueuesService;
        this.TenantService = TenantService;
        this.AttendanceHourService = AttendanceHourService;
        this.AccountService2 = AccountService2;
        this.MarketplaceTemplatesService = MarketplaceTemplatesService;
        this.$rootScope = $rootScope;
        this.$translate = $translate;
        this.AI_AGENT_STORAGE_BASE_URL = AI_AGENT_STORAGE_BASE_URL;
        this.MEDIA_PROFILE_FOR_AVATARS = MEDIA_PROFILE_FOR_AVATARS;
        this.enableCustomerInactivityFeatures = false;
        this.isNewPublishFlowAiAgentEnabled = false;
        this.isConfigurationsApplicationSetEnabled = false;
        this.$onInit();
    }
    async $onInit() {
        await this.checkFeatures();
    }
    async checkFeatures() {
        if (!this.$rootScope.featureToggleInitialized) {
            await this.InitializeUserService.initFeatureToggle();
        }
        const [isForwardToDeskAttendanceBoxEnabled, redirectOutOfAttendanceHourEnabled, redirectNoAgentsAvailableEnabled, valueOfCustomerInactivityDate, isNewPublishFlowAiAgentEnabled, isConfigurationsApplicationSetEnabled,] = await Promise.all([
            BuilderFeatures.isForwardToDeskAttendanceBoxEnabled(),
            BuilderFeatures.isRedirectOutOfAttendanceHourEnabled(),
            BuilderFeatures.isRedirectNoAgentsAvailableEnabled(),
            ApplicationCreationFeatures.valueOfCustomerInactivityDate(),
            ApplicationCreationFeatures.newPublishFlowAiAgentIsEnabled(),
            BuilderFeatures.isUseConfigurationsDomainOnMiscOperationsSetEnabled(),
        ]);
        this.isForwardToDeskAttendanceBoxEnabled = isForwardToDeskAttendanceBoxEnabled;
        this.redirectOutOfAttendanceHourEnabled = redirectOutOfAttendanceHourEnabled;
        this.redirectNoAgentsAvailableEnabled = redirectNoAgentsAvailableEnabled;
        this.getCustomerInactivityDateEnable = valueOfCustomerInactivityDate;
        this.isNewPublishFlowAiAgentEnabled = isNewPublishFlowAiAgentEnabled;
        this.isConfigurationsApplicationSetEnabled = isConfigurationsApplicationSetEnabled;
    }
    async createApplication(applicationData, template) {
        // Step 1: Validate and prepare application data
        await this.prepareApplicationData(applicationData, template);
        const tenantId = this.getTenantId();
        const expectedShortName = applicationData.shortName; // Store expected name for validation
        // Step 2: Upload image (non-critical, continue on failure)
        await this.uploadApplicationImageSafely(applicationData);
        // Step 3: Create application on Iris
        let createdApplication = null;
        try {
            createdApplication = await this.createApplicationOnIris(applicationData, tenantId);
        }
        catch (err) {
            console.error('[CreateApplication] Application creation failed', err);
            // InitialCreation errors are critical - cleanup and throw
            // Only cleanup if we actually created something in this session
            if (createdApplication && createdApplication.shortName === expectedShortName) {
                await this.cleanupFailedApplication(createdApplication);
            }
            throw err;
        }
        // Step 4: Set application extras (non-critical)
        await this.setApplicationExtras(applicationData);
        // Step 5: Activate application features (may throw critical or non-critical errors)
        try {
            await this.activateApplicationFeatures(applicationData);
        }
        catch (err) {
            // Error handling strategy:
            // - CRITICAL: BuilderActivation (bot won't work) -> cleanup + throw
            const isCriticalError = err instanceof CreateApplicationError &&
                err.step === CreateApplicationSteps.BuilderActivation;
            if (isCriticalError) {
                console.error('[CreateApplication] Builder activation failed, cleaning up application', err);
                // Verify this is the app we created in this session before cleanup
                if (createdApplication && createdApplication.shortName === expectedShortName) {
                    await this.cleanupFailedApplication(createdApplication);
                }
                else {
                    console.error('[CreateApplication] Safety check failed - shortName mismatch, skipping cleanup');
                }
                throw err;
            }
            else {
                console.warn('[CreateApplication] Attendance activation failed, continuing without help desk features', err);
            }
        }
        // Step 6: Apply marketplace template (non-critical)
        try {
            await this.applyMarketplaceTemplate(applicationData);
        }
        catch (err) {
            console.warn('[CreateApplication] Template application failed, continuing', err);
        }
        return createdApplication;
    }
    async prepareApplicationData(applicationData, template) {
        try {
            applicationData.marketplace = await this.getMarketplaceTemplateAsync(template);
            applicationData.template = applicationData.marketplace.type;
            applicationData.shortName = applicationData.name.toLowerCase();
            applicationData.messagingHubCommandWithDelegation = await MessagingHubFeatures.GetMessagingHubCommandWithDelegation();
            this.validateApplicationName(applicationData.name);
        }
        catch (err) {
            console.error('[CreateApplication] Data validation failed', err);
            throw new CreateApplicationError(err, CreateApplicationSteps.DataValidation);
        }
    }
    getTenantId() {
        return this.ContextProcessorService.context === Contexts.Tenants
            ? this.ContextProcessorService.tenant.id
            : undefined;
    }
    async uploadApplicationImageSafely(application) {
        try {
            await this.setApplicationImage(application);
        }
        catch (err) {
            console.warn('[CreateApplication] Image upload failed, continuing without image', err);
        }
    }
    async createApplicationOnIris(application, tenantId) {
        try {
            const createdApplication = await this.ApplicationService2.setApplication(application, tenantId);
            await this.MessagingHubService.resetClient(createdApplication);
            // Merge input application with new created object
            const mergedApplication = { ...application, ...createdApplication };
            if (createdApplication.tenantId && this.getCustomerInactivityDateEnable) {
                this.enableInactivityFeaturesInTemplate(createdApplication.tenantId);
            }
            // Return the merged application object (has both input data + server response)
            return mergedApplication;
        }
        catch (err) {
            console.error('[CreateApplication] Failed to create application on Iris', err);
            throw new CreateApplicationError(err, CreateApplicationSteps.InitialCreation);
        }
    }
    async setApplicationExtras(application) {
        try {
            const applicationAccount = await this.ApplicationService2.getAccount();
            if (applicationAccount) {
                applicationAccount.extras['#marketplace.id'] = application.marketplace.id;
                await this.ApplicationService2.setAccount(applicationAccount);
            }
        }
        catch (err) {
            // Non-critical: application extras are optional metadata
            console.warn('[CreateApplication] Failed to set application extras', err);
        }
    }
    async activateApplicationFeatures(application) {
        const isBuilderOrPipeline = application.template === ApplicationType.Builder ||
            application.template === ApplicationType.Pipeline;
        if (!isBuilderOrPipeline) {
            return;
        }
        try {
            await this.ChatbotSettingsService.setBuilderActive(application, true);
        }
        catch (err) {
            console.error('[CreateApplication] Builder activation failed', err);
            throw new CreateApplicationError(err, CreateApplicationSteps.BuilderActivation);
        }
        try {
            await this.activateAttendance();
        }
        catch (err) {
            console.warn('[CreateApplication] Attendance activation failed', err);
            throw new CreateApplicationError(err, CreateApplicationSteps.AttendanceActivation);
        }
    }
    async applyMarketplaceTemplate(application) {
        if (application.marketplace.id === NO_MARKETPLACE_TEMPLATE_ID) {
            return;
        }
        try {
            await this.MarketplaceTemplatesService.processTemplate(application);
        }
        catch (err) {
            console.warn('[CreateApplication] Template processing failed', err);
            throw new CreateApplicationError(err, CreateApplicationSteps.TemplateApplication);
        }
    }
    async cleanupFailedApplication(createdApplication) {
        if (!createdApplication) {
            return;
        }
        // Safety check: only cleanup if we have a shortName that matches what we're creating
        // This prevents accidentally deleting wrong applications
        if (!createdApplication.shortName) {
            console.warn('[CreateApplication] Cleanup aborted - application has no shortName');
            return;
        }
        try {
            await this.ApplicationService2.deleteAndRevokeApplication(createdApplication);
        }
        catch (cleanupErr) {
            console.error('[CreateApplication] Failed to cleanup application after error', cleanupErr);
        }
    }
    async validateApplicationName(name) {
        const letterFirstPosition = /(^[a-zA-Z])/;
        if (!letterFirstPosition.exec(name)) {
            throw new CreateApplicationError(new Error('Application name is not valid'), CreateApplicationSteps.DataValidation);
        }
    }
    async getMarketplaceTemplateAsync(template) {
        switch (template) {
            case 'blip_deskCustomerService':
                return {
                    id: 'blip_deskCustomerService',
                    type: ApplicationType.Builder,
                    template: this.getDeskCustomerServiceTemplate(),
                };
            case 'blip_aiAgent_publish':
            case 'aiAgent':
                if (await ApplicationCreationFeatures.aiAgentTemplateHostingIsEnabled()) {
                    return {
                        id: '0',
                        type: template,
                    };
                }
                return {
                    id: 'blip_aiAgent_publish',
                    type: ApplicationType.Builder,
                    template: await this.getAiAgentTemplateAsync(),
                };
            default:
                return {
                    id: '0',
                    type: template,
                };
        }
    }
    async getAiAgentTemplateAsync() {
        try {
            const aiAgentContainerBaseUrl = this.AI_AGENT_STORAGE_BASE_URL;
            const response = await fetch(`${aiAgentContainerBaseUrl}/aiagentstemplates/aiAgentTemplate_publish.json`);
            if (!response.ok) {
                throw new Error('Failed to fetch AI agent template');
            }
            if (this.isNewPublishFlowAiAgentEnabled) {
                const publishStep = AiAgentPublishFlowTemplate.steps.find(step => step.type == 'PublishFlow');
                if (publishStep && publishStep.arguments) {
                    publishStep.arguments.flow = await response.json();
                }
                return AiAgentPublishFlowTemplate;
            }
            return await response.json();
        }
        catch (err) {
            throw err;
        }
    }
    getDeskCustomerServiceTemplate() {
        if (this.isForwardToDeskAttendanceBoxEnabled &&
            this.redirectOutOfAttendanceHourEnabled &&
            this.redirectNoAgentsAvailableEnabled) {
            return CustomerServiceForwardToDeskTemplatePT;
        }
        return CustomerServiceTemplatePT;
    }
    async setApplicationImage(application) {
        if (!application.image) {
            return;
        }
        const mediaUri = await this.ApplicationService2.uploadFile(application.image, { profile: this.MEDIA_PROFILE_FOR_AVATARS });
        if (mediaUri) {
            application.imageUri = mediaUri;
        }
    }
    async activateAttendance() {
        const configResource = this.enableCustomerInactivityFeatures ? {
            'Lime.IsActive': true,
            'DefaultProvider': 'Lime',
            'DistributionType': 'Redis',
            'InactivityMessage': await this.$translate('createApplication.customerInactivity.InactivityMessage'),
            'InactivityMessageDowntime': 15,
            'InactivityTags': [await this.$translate('createApplication.customerInactivity.inactivityTag')],
            'MaxClientMinutesDowntime': 25,
            'ResetClientInactivityTimerOnAgentInteraction': true,
            'ResetClientInactivityTimerOnFirstAgentInteraction': true,
            'UseInactivityMessage': true,
            'UseInactivityTags': true,
            'CustomerMaxResponseDelay': 1200,
            'CustomerWarningResponseDelay': 600,
            'AutomaticClosedTicketIsAllowed': true,
            'AutomaticClosedTicketIsEnabled': true,
            'AgentMaxResponseDelay': 900,
        } : {
            'Lime.IsActive': true,
            'DefaultProvider': 'Lime',
            'DistributionType': 'Redis',
        };
        await this.MessagingHubService.processCommand({
            method: 'set',
            uri: 'lime://postmaster@desk.msging.net/configuration',
            type: 'application/json',
            resource: configResource,
            to: this.isConfigurationsApplicationSetEnabled ? 'postmaster@configurations.msging.net' : undefined,
        });
        const isBillablePlanContract = await this.TenantService.checkIfBillableContract();
        if (!isBillablePlanContract) {
            const account = await await this.AccountService2.me();
            const attendant = {
                teams: ['Default'],
                identity: account.identity,
                isEnabled: true,
            };
            await this.TeamService.set([attendant]);
        }
        const queue = {
            id: '',
            isActive: true,
            name: 'Default',
            numberAttendants: 0,
        };
        await this.QueuesService.save(queue);
        if (this.redirectOutOfAttendanceHourEnabled) {
            const defaultStartTime = '08:00:00';
            const defaultEndTime = '18:00:00';
            const weekDaysKeys = Array.from(Array(5), (_, index) => index + 1);
            const attendanceHourScheduleItems = weekDaysKeys.map(weekDayKey => ({
                startTime: defaultStartTime,
                endTime: defaultEndTime,
                dayOfWeek: weekDayKey,
            }));
            const attendanceHourDefault = {
                attendanceHour: {
                    title: await this.$translate('createApplication.attendanceHourDefault.title'),
                    description: await this.$translate('createApplication.attendanceHourDefault.description'),
                    isMain: true,
                },
                attendanceHourScheduleItems: attendanceHourScheduleItems,
            };
            await this.AttendanceHourService.set(attendanceHourDefault);
        }
    }
    async enableInactivityFeaturesInTemplate(tenantId) {
        if (tenantId) {
            const tenant = await this.TenantService.get(tenantId);
            this.enableCustomerInactivityFeatures = new Date(tenant.creationDate).toJSON() >= this.getCustomerInactivityDateEnable.toJSON();
        }
    }
}
